import { useState, useEffect } from 'react';
import {
  UserGroupIcon,
  KeyIcon,
  PlusIcon,
  ArrowPathIcon,
  ClipboardDocumentIcon,
  CheckIcon,
  ExclamationTriangleIcon,
  TrashIcon,
  EyeIcon,
  EyeSlashIcon,
  PencilSquareIcon,
  PrinterIcon,
} from '@heroicons/react/24/outline';
import { supabase } from '../../lib/supabase';
import { toast } from 'react-hot-toast';

interface Scorer {
  id: string;
  email: string;
  full_name: string;
  role: string;
  is_active: boolean;
  created_at: string;
}

interface CreateResult {
  email: string;
  password: string;
  success: boolean;
  error?: string;
}

interface EditState {
  id: string;
  full_name: string;
  new_password: string;
}

export default function ScorerAccountsManager() {
  const [scorers, setScorers] = useState<Scorer[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [resettingAll, setResettingAll] = useState(false);
  const [resettingId, setResettingId] = useState<string | null>(null);
  const [lastPassword, setLastPassword] = useState<string | null>(null);
  const [createResults, setCreateResults] = useState<CreateResult[] | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [editing, setEditing] = useState<EditState | null>(null);
  const [saving, setSaving] = useState(false);

  // Create config
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createCount, setCreateCount] = useState(18);
  const [createPrefix, setCreatePrefix] = useState('scorer');
  const [createDomain, setCreateDomain] = useState('ibomopen.com');
  const [createPassword, setCreatePassword] = useState('');

  useEffect(() => {
    fetchScorers();
  }, []);

  const getHeaders = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    return {
      'Authorization': `Bearer ${session?.access_token}`,
      'Content-Type': 'application/json',
      'Apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
    };
  };

  const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/manage-scorers`;

  const fetchScorers = async () => {
    try {
      setLoading(true);
      const headers = await getHeaders();
      const res = await fetch(apiUrl, { headers });
      const data = await res.json();
      if (data.success) {
        setScorers(data.scorers || []);
      } else {
        toast.error(data.message || 'Failed to load scorers');
      }
    } catch {
      toast.error('Failed to load scorer accounts');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateScorers = async () => {
    if (createCount < 1 || createCount > 50) {
      toast.error('Count must be between 1 and 50');
      return;
    }
    setCreating(true);
    setCreateResults(null);
    try {
      const headers = await getHeaders();
      const res = await fetch(apiUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          count: createCount,
          prefix: createPrefix,
          domain: createDomain,
          password: createPassword || undefined,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setCreateResults(data.results);
        setLastPassword(data.password);
        toast.success(data.message);
        fetchScorers();
        setShowCreateForm(false);
      } else {
        toast.error(data.message || 'Failed to create scorers');
      }
    } catch {
      toast.error('Failed to create scorer accounts');
    } finally {
      setCreating(false);
    }
  };

  const handleResetPassword = async (id: string) => {
    setResettingId(id);
    try {
      const headers = await getHeaders();
      const res = await fetch(apiUrl, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ id }),
      });
      const data = await res.json();
      if (data.success) {
        setLastPassword(data.password);
        toast.success(`Password reset. New password: ${data.password}`);
      } else {
        toast.error(data.message || 'Failed to reset password');
      }
    } catch {
      toast.error('Failed to reset password');
    } finally {
      setResettingId(null);
    }
  };

  const handleSaveEdit = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const headers = await getHeaders();
      const payload: Record<string, unknown> = { id: editing.id, full_name: editing.full_name };
      if (editing.new_password.trim()) {
        if (editing.new_password.trim().length < 8) {
          toast.error('Password must be at least 8 characters');
          setSaving(false);
          return;
        }
        payload.new_password = editing.new_password.trim();
      }
      const res = await fetch(apiUrl, {
        method: 'PUT',
        headers,
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (data.success) {
        setScorers(prev => prev.map(s => s.id === editing.id ? { ...s, full_name: editing.full_name } : s));
        if (editing.new_password.trim()) {
          setLastPassword(data.password || editing.new_password.trim());
          toast.success(`Saved. Password set to: ${data.password || editing.new_password.trim()}`);
        } else {
          toast.success('Name updated');
        }
        setEditing(null);
      } else {
        toast.error(data.message || 'Failed to save');
      }
    } catch {
      toast.error('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleResetAllPasswords = async () => {
    setResettingAll(true);
    try {
      const headers = await getHeaders();
      const res = await fetch(apiUrl, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ reset_all: true }),
      });
      const data = await res.json();
      if (data.success) {
        setLastPassword(data.password);
        toast.success(`All scorer passwords reset to: ${data.password}`);
      } else {
        toast.error(data.message || 'Failed to reset passwords');
      }
    } catch {
      toast.error('Failed to reset passwords');
    } finally {
      setResettingAll(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const headers = await getHeaders();
      const res = await fetch(apiUrl, {
        method: 'DELETE',
        headers,
        body: JSON.stringify({ id }),
      });
      const data = await res.json();
      if (data.success) {
        toast.success('Scorer account deleted');
        setScorers(prev => prev.filter(s => s.id !== id));
      } else {
        toast.error(data.message || 'Failed to delete');
      }
    } catch {
      toast.error('Failed to delete scorer');
    } finally {
      setConfirmDelete(null);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const copyAllCredentials = () => {
    if (!createResults || !lastPassword) return;
    const lines = createResults
      .filter(r => r.success)
      .map(r => `${r.email} / ${r.password}`)
      .join('\n');
    navigator.clipboard.writeText(lines);
    toast.success('All credentials copied');
  };

  const printCredentials = () => {
    if (scorers.length === 0) return;

    const password = lastPassword || prompt('Enter the shared password to print on the credentials sheet:');
    if (!password) return;

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const rows = scorers.map((s, i) => `
      <tr>
        <td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;text-align:center;font-size:13px;">${i + 1}</td>
        <td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;font-size:13px;font-weight:500;">${s.full_name || `Scorer ${String(i + 1).padStart(2, '0')}`}</td>
        <td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;font-family:monospace;font-size:13px;">${s.email}</td>
        <td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;font-family:monospace;font-size:13px;font-weight:600;">${password}</td>
      </tr>
    `).join('');

    printWindow.document.write(`
      <html><head><title>Scorer Credentials - Ibom Open</title>
      <style>
        body { font-family: 'Helvetica Neue', Arial, sans-serif; padding: 40px; color: #1a1a1a; }
        .header { border-bottom: 3px solid #1A472A; padding-bottom: 16px; margin-bottom: 24px; }
        .header h1 { color: #1A472A; margin: 0; font-size: 22px; }
        .header p { color: #666; margin: 4px 0 0; font-size: 13px; }
        table { width: 100%; border-collapse: collapse; margin-top: 16px; }
        th { background: #1A472A; color: white; padding: 10px 12px; text-align: left; font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; }
        th:first-child { text-align: center; width: 40px; }
        .note { margin-top: 24px; padding: 16px; background: #fffbf5; border: 1px solid #fed7aa; border-radius: 8px; font-size: 12px; color: #92400e; }
        .footer { margin-top: 32px; padding-top: 16px; border-top: 1px solid #e5e7eb; font-size: 11px; color: #888; text-align: center; }
        @media print { body { padding: 20px; } }
      </style></head><body>
        <div class="header">
          <h1>Scorer Login Credentials</h1>
          <p>Ibom Open Golf Tournament | Generated: ${new Date().toLocaleDateString('en-NG', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
        </div>
        <table>
          <thead>
            <tr>
              <th>#</th>
              <th>Name</th>
              <th>Username (Email)</th>
              <th>Password</th>
            </tr>
          </thead>
          <tbody>
            ${rows}
          </tbody>
        </table>
        <div class="note">
          <strong>Important:</strong> These credentials provide access to the scoring system.
          Each scorer should log in at <strong>ibomopen.com</strong> using their email and password above.
          Please keep this document secure and distribute individually to each scorer.
        </div>
        <div class="footer">
          <p>Ibom Open Golf Tournament | Ibom Golf & Country Club, Uyo, Akwa Ibom State, Nigeria</p>
        </div>
      </body></html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-golf-gray">Scorer Accounts</h2>
          <p className="text-sm text-gray-500 mt-1">
            Generate and manage scorer login credentials. No email confirmation required.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {scorers.length > 0 && (
            <button
              onClick={handleResetAllPasswords}
              disabled={resettingAll}
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-lg hover:bg-amber-100 transition-colors disabled:opacity-50"
            >
              <ArrowPathIcon className={`w-4 h-4 ${resettingAll ? 'animate-spin' : ''}`} />
              Reset All Passwords
            </button>
          )}
          {scorers.length > 0 && (
            <button
              onClick={printCredentials}
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <PrinterIcon className="w-4 h-4" />
              Print Credentials
            </button>
          )}
          <button
            onClick={() => setShowCreateForm(true)}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-golf-orange rounded-lg hover:bg-golf-orange/90 transition-colors"
          >
            <PlusIcon className="w-4 h-4" />
            Generate Scorers
          </button>
        </div>
      </div>

      {/* Last generated password */}
      {lastPassword && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <KeyIcon className="w-5 h-5 text-green-600" />
              <span className="text-sm font-medium text-green-800">Current Password:</span>
              <code className="px-2 py-0.5 bg-green-100 rounded text-sm font-mono text-green-900">
                {showPassword ? lastPassword : '••••••••••••'}
              </code>
              <button
                onClick={() => setShowPassword(!showPassword)}
                className="p-1 text-green-600 hover:text-green-800"
              >
                {showPassword ? <EyeSlashIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
              </button>
            </div>
            <button
              onClick={() => copyToClipboard(lastPassword)}
              className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-green-700 bg-green-100 rounded hover:bg-green-200 transition-colors"
            >
              <ClipboardDocumentIcon className="w-3.5 h-3.5" />
              Copy
            </button>
          </div>
        </div>
      )}

      {/* Create results */}
      {createResults && (
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-700">
              Generated Accounts ({createResults.filter(r => r.success).length}/{createResults.length})
            </h3>
            <button
              onClick={copyAllCredentials}
              className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              <ClipboardDocumentIcon className="w-3.5 h-3.5" />
              Copy All Credentials
            </button>
          </div>
          <div className="max-h-60 overflow-y-auto space-y-1">
            {createResults.map((result, i) => (
              <div
                key={i}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${
                  result.success ? 'bg-green-50' : 'bg-red-50'
                }`}
              >
                {result.success ? (
                  <CheckIcon className="w-4 h-4 text-green-500 flex-shrink-0" />
                ) : (
                  <ExclamationTriangleIcon className="w-4 h-4 text-red-500 flex-shrink-0" />
                )}
                <span className="font-mono text-xs flex-1">{result.email}</span>
                {result.success && (
                  <span className="font-mono text-xs text-gray-500">{result.password}</span>
                )}
                {result.error && (
                  <span className="text-xs text-red-600 truncate max-w-48">{result.error}</span>
                )}
              </div>
            ))}
          </div>
          <button
            onClick={() => setCreateResults(null)}
            className="mt-3 text-xs text-gray-500 hover:text-gray-700"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Create form modal */}
      {showCreateForm && (
        <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
          <h3 className="text-lg font-medium text-golf-gray mb-4">Generate Scorer Accounts</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Number of accounts</label>
              <input
                type="number"
                value={createCount}
                onChange={e => setCreateCount(Number(e.target.value))}
                min={1}
                max={50}
                className="w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email prefix</label>
              <input
                type="text"
                value={createPrefix}
                onChange={e => setCreatePrefix(e.target.value)}
                placeholder="scorer"
                className="w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange"
              />
              <p className="text-xs text-gray-400 mt-1">Emails: {createPrefix}01@{createDomain}, {createPrefix}02@{createDomain}, etc.</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email domain</label>
              <input
                type="text"
                value={createDomain}
                onChange={e => setCreateDomain(e.target.value)}
                placeholder="ibomopen.com"
                className="w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Password (optional)</label>
              <input
                type="text"
                value={createPassword}
                onChange={e => setCreatePassword(e.target.value)}
                placeholder="Auto-generate if blank"
                className="w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange"
              />
              <p className="text-xs text-gray-400 mt-1">Same password for all accounts. Leave blank to auto-generate.</p>
            </div>
          </div>
          <div className="flex justify-end gap-3 mt-6">
            <button
              onClick={() => setShowCreateForm(false)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleCreateScorers}
              disabled={creating}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-golf-orange rounded-lg hover:bg-golf-orange/90 disabled:opacity-50"
            >
              {creating ? (
                <>
                  <ArrowPathIcon className="w-4 h-4 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <UserGroupIcon className="w-4 h-4" />
                  Generate {createCount} Accounts
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Scorers table */}
      {loading ? (
        <div className="flex items-center justify-center h-40">
          <div className="animate-spin rounded-full h-10 w-10 border-4 border-golf-orange border-t-transparent" />
        </div>
      ) : scorers.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-8 text-center">
          <UserGroupIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 mb-1">No scorer accounts created yet</p>
          <p className="text-sm text-gray-400">Click "Generate Scorers" to create accounts.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Password</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {scorers.map((scorer, idx) => {
                  const isEditing = editing?.id === scorer.id;
                  return (
                    <tr key={scorer.id} className={isEditing ? 'bg-amber-50/50' : 'hover:bg-gray-50'}>
                      <td className="px-4 py-3 text-sm text-gray-500">{idx + 1}</td>
                      <td className="px-4 py-3">
                        <span className="text-sm font-mono text-gray-700">{scorer.email}</span>
                      </td>
                      <td className="px-4 py-3">
                        {isEditing ? (
                          <input
                            type="text"
                            value={editing.full_name}
                            onChange={e => setEditing({ ...editing, full_name: e.target.value })}
                            className="w-full text-sm rounded border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange py-1 px-2"
                            placeholder="Scorer name"
                          />
                        ) : (
                          <span className="text-sm text-gray-600">{scorer.full_name || '-'}</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {isEditing ? (
                          <input
                            type="text"
                            value={editing.new_password}
                            onChange={e => setEditing({ ...editing, new_password: e.target.value })}
                            className="w-full text-sm rounded border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange py-1 px-2 font-mono"
                            placeholder="New password (min 8 chars)"
                          />
                        ) : (
                          <span className="text-xs text-gray-400 italic">Set via edit</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          scorer.is_active
                            ? 'bg-green-100 text-green-700'
                            : 'bg-gray-100 text-gray-500'
                        }`}>
                          {scorer.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {isEditing ? (
                            <>
                              <button
                                onClick={handleSaveEdit}
                                disabled={saving}
                                className="px-2.5 py-1 text-xs font-medium text-white bg-golf-orange rounded-lg hover:bg-golf-orange/90 disabled:opacity-50"
                              >
                                {saving ? 'Saving...' : 'Save'}
                              </button>
                              <button
                                onClick={() => setEditing(null)}
                                className="px-2.5 py-1 text-xs font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200"
                              >
                                Cancel
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                onClick={() => setEditing({ id: scorer.id, full_name: scorer.full_name || '', new_password: '' })}
                                className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                title="Edit name & password"
                              >
                                <PencilSquareIcon className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleResetPassword(scorer.id)}
                                disabled={resettingId === scorer.id}
                                className="p-1.5 text-amber-600 hover:bg-amber-50 rounded-lg transition-colors disabled:opacity-50"
                                title="Auto-generate new password"
                              >
                                <KeyIcon className={`w-4 h-4 ${resettingId === scorer.id ? 'animate-pulse' : ''}`} />
                              </button>
                              {confirmDelete === scorer.id ? (
                                <button
                                  onClick={() => handleDelete(scorer.id)}
                                  className="px-2 py-1 text-xs font-medium text-red-700 bg-red-100 rounded hover:bg-red-200"
                                >
                                  Confirm
                                </button>
                              ) : (
                                <button
                                  onClick={() => setConfirmDelete(scorer.id)}
                                  className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                  title="Delete account"
                                >
                                  <TrashIcon className="w-4 h-4" />
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
